<template>
	<a
		class="promotion-notice"
		:href="link"
		:title="linkTitle"
		v-bind="ga4Attributes"
	>
		<div class="promotion-notice__content">
			<h4 v-text="cardTitle" />
			<p v-text="cardText" />
			<LinkButton
				class="promotion-notice__btn"
				theme="inherit"
				tabindex="-1"
				:with-icon="false"
				:label="buttonLabel"
			/>
		</div>
		<div
			v-if="badgeText?.trim()"
			class="promotion-notice__badge"
		>
			<p
				class="promotion-notice__badge-text"
				v-text="cardBadgeText"
			/>
		</div>
	</a>
</template>

<script lang="ts" setup>
import LinkButton from '@lmt-rpb/Atoms/LinkButton/LinkButton.vue';
import { gaSelectContentAttr } from 'src/utils/analytics';
import { computed } from 'vue';

export interface Props {
	title: string;
	description: string;
	badgeText?: string;
	linkText?: string;
	link: string;
	linkTitle?: string;
}

const { linkText = 'Mehr erfahren', title, description, badgeText } = defineProps<Props>();

const TEXT_LIMITS = {
	BUTTON: 35,
	TITLE: 22,
	TEXT: 100,
	BADGE: 40,
} as const;

const ga4Attributes = gaSelectContentAttr({
	cId: title,
	cType: 'ux_plugin_promotion_notice_click'
})
const buttonLabel = computed(() => linkText.substring(0, TEXT_LIMITS.BUTTON));
const cardTitle = computed(() => title.substring(0, TEXT_LIMITS.TITLE));
const cardText = computed(() => description.substring(0, TEXT_LIMITS.TEXT));
const cardBadgeText = computed(() => badgeText?.substring(0, TEXT_LIMITS.BADGE));
</script>

<style lang="scss" scoped>
.promotion-notice {
	display: flex;
	justify-content: space-between;
	align-items: stretch;
	outline: 0.2rem solid transparent;
	padding: 1.6rem;
	z-index: 0;
	position: relative;
	gap: 1.6rem;
	border-radius: $border-radius-large;
	text-decoration: none;
	color: $color-white;
	font-family: $font-family;
	transition: all 0.2s ease-out;
	outline-offset: 0.2rem;
	box-shadow: $box-shadow;

	* > * {
		pointer-events: none;
	}

	&::after,
	&::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
	}

	&::before {
		z-index: -2;
		background: linear-gradient(84.6deg, $color-primary 0%, $color-primary-d0 100%);
	}

	&::after {
		z-index: -1;
		transition: all 0.2s ease-out;
		background: transparent;
	}

	p,
	h4 {
		margin: 0;
		padding: 0;
	}

	& > * {
		pointer-events: none;
	}

	&__content {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		align-items: stretch;

		h4 {
			font-size: $font-medium-3;
			font-weight: $font-weight-semibold;
			font-family: $font-family-special;
			line-height: 2.4rem;
			margin-bottom: 0.4rem;
		}

		p {
			font-size: $font-small-2;
			font-weight: $font-weight-regular;
			line-height: 2.1rem;
			margin-bottom: 1.6rem;
		}

		.promotion-notice__btn {
			margin-top: auto;
		}
	}

	&__badge {
		display: flex;
		flex-shrink: 0;
		justify-content: center;
		align-items: center;
		align-self: center;
		border-radius: 50%;
		width: 12.8rem;
		height: 12.8rem;
		padding: 1.6rem;
		background-color: $color-secondary;

		&-text {
			font-size: $font-small-2;
			font-weight: $font-weight-semibold;
			font-family: $font-family-special;
			color: $color-text-inverted;
			line-height: 2.1rem;
			text-align: center;
		}
	}

	&:focus-visible {
		outline-color: $color-primary !important;
	}

	&:hover {
		&::after {
			background: $color-primary-d0;
		}

		box-shadow: $box-shadow-hover;
	}

	@include media-query-up($breakpoint-extralarge) {
		&__content {
			h4 {
				font-size: $font-medium-2;
				line-height: 2.6rem;
			}
		}
	}
}
</style>
